import { fabric } from 'fabric';
import { Injector } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs';
import { ItemModel } from '../../../../models/itemModel';
import { ResourceItemModel } from '../../../../models/resource';
import { FrameItem } from '../../../../models/session';
import {
  ImageItemCanvasComponent,
  ImageLoadingState,
} from '../../image-item-canvas/image-item-canvas.component';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { ItemsCanvasService } from '../../../../services/items-canvas.service';
import { FLAGS } from '../../../../services/flags.service';
import { environment } from '../../../../../environments/environment';
import { BoardItemComponentClass, FabricControls, MAX_BOARD_ITEMS_COUNT } from './board.item';
import { ImageConfigurations } from './board-image-configurations';
import { CreationContext } from './board-item-configurations';

// Defined only so that it has a common constructor signature with fabric.Object and fabric.Rect
export class FabricImage extends fabric.Image {
  constructor(value: any) {
    super('', { ...value, crossOrigin: 'anonymous', fill: '#fff', opacity: 1 });
  }

  static _fromObject(
    className: string,
    object: fabric.Object,
    callback?: Function,
    extraParam?: any,
  ): fabric.Object {
    return fabric.Object._fromObject(className, object, callback, extraParam);
  }
}

// Only middle controls are invisible for images
const ImageInvisibleControls = [
  FabricControls.MiddleTop,
  FabricControls.MiddleBottom,
  FabricControls.MiddleLeft,
  FabricControls.MiddleRight,
];

export class BoardImage extends BoardItemComponentClass<
  ImageConfigurations,
  ImageItemCanvasComponent
>(FabricImage) {
  AngularComponentClass = ImageItemCanvasComponent;

  private boardItemsInteractionsManagerService: BoardItemsInteractionsManager;
  private itemsCanvasService: ItemsCanvasService;

  loadingState: ImageLoadingState = ImageLoadingState.DidNotAttemptToLoad;
  loadingFailureSubscription?: Subscription;
  loadedImageSubscription?: Subscription;

  imageUntouchedWidth?: number;
  imageUntouchedHeight?: number;
  widthBeforeConvertingToFabricImage?: number;
  heightBeforeConvertingToFabricImage?: number;

  protected customLoadingLayout: boolean = this.isPdfPage();
  override createHtmlWhenInViewport = true;

  constructor(
    injector: Injector,
    configurations: ImageConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.Image, configurations, ImageInvisibleControls, creationContext);

    this.focusOnInsertion =
      creationContext.isNewlyCreatedItem && creationContext.renderInNearestFreeSpace;

    this.boardItemsInteractionsManagerService = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );
    this.itemsCanvasService = injector.get<ItemsCanvasService>(ItemsCanvasService);
  }

  override onFabricAdded() {
    super.onFabricAdded();

    this.signImageUrlIfNecessary();
    this.setInteractivityBasedOnLoadingState();
  }

  override onHtmlAdded() {
    super.onHtmlAdded();

    this.subscribeToImageLoadingState();
  }

  protected onFabricRemoved() {
    super.onFabricRemoved();

    this.loadingFailureSubscription?.unsubscribe();
    this.loadedImageSubscription?.unsubscribe();
  }

  override openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = false;
    this.lockScalingX = false;
    this.lockScalingY = false;
    this.lockUniScaling = true;
    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.lockScalingFlip = false;

    this.evented = true;
    this.propagateCanvasMouseDownToObject = false; // When the object is locked we don't need this as the behavior will be normal and we don't want an extra performance overhead.
  }

  closeLock() {
    super.closeLock();

    // when the image is locked we need to treat it as if it doesn't exist, to enable multi-select of scribbles on top of it
    // We instead, can implement the needed actions on mouse down in the canvasMouseDown method.
    this.evented = false;
    this.propagateCanvasMouseDownToObject = true;
  }

  protected onCanvasMouseDown(mouseDownAffectedOtherObjects: boolean) {
    super.onCanvasMouseDown(mouseDownAffectedOtherObjects);
    if (mouseDownAffectedOtherObjects) {
      // the click was on another object above the image and the object didn't receive another click
      return;
    }

    this.showLockSnackBarIfObjectLocked();

    this.canvas?.setActiveObject(this);
    this.canvas?.requestRenderAll(); // to render the blue border
  }

  getZIndexObservable$(): Observable<number> {
    return combineLatest([
      this.boardItemsInteractionsManagerService.itemInEditMode$(this.configurations.uid),
      this.itemOrder$,
    ]).pipe(
      map(([interactionsEnabled, itemOrder]) => {
        if (this.isOnStaticCanvas()) {
          return itemOrder;
        }
        if (this.flagsService.isFlagEnabled(FLAGS.RENDER_IMAGES_IN_FABRIC)) {
          // When all other images are rendered in fabric, the images that are in the state of loading or failed to load
          // will appear behind the loaded images, because the fabric canvas is above the HTML canvas,
          // and we are raising the z-index to prevent that
          return MAX_BOARD_ITEMS_COUNT + 1;
        }
        if (interactionsEnabled) {
          return MAX_BOARD_ITEMS_COUNT + 1;
        }
        const index = itemOrder - MAX_BOARD_ITEMS_COUNT - 1;
        return Math.min(-1, index);
      }),
      distinctUntilChanged(),
    );
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();
    return {
      ...encodedBoardItem,
      model: ItemModel.Resource,
      resource_state: {
        resource_url: this.configurations.url,
        // Set only for PDF/office pages rendered as standalone images, so the
        // board item stays traceable back to the original uploaded file.
        source_url: this.configurations.sourceUrl,
        resource_item_model: ResourceItemModel.IMAGE,
      },
      migratedFromHtmlToFabric: this.configurations.migratedFromHtmlToFabric,
    };
  }

  shouldEnterEditMode(mouseY: number, itemY: number, itemSelected: boolean): boolean {
    return this.loadingState === ImageLoadingState.FailedToLoad; // to make the user able to click the retry button
  }

  subscribeToImageLoadingState() {
    if (!this.isOnStaticCanvas()) {
      // We need to know whether the image have failed to load or not,
      // because when the image fails, it needs to be intractable to enable the user from clicking the retry button
      this.loadingFailureSubscription?.unsubscribe();
      this.loadingFailureSubscription = this.componentRef?.instance.loadingStateChanged.subscribe(
        (loadingState) => {
          this.loadingState = loadingState;
          this.setPointerEventsBasedOnLoadingState();
          this.setInteractivityBasedOnLoadingState();
        },
      );
    }

    if (this.flagsService.isFlagEnabled(FLAGS.RENDER_IMAGES_IN_FABRIC)) {
      this.loadedImageSubscription?.unsubscribe();
      this.loadedImageSubscription = this.componentRef?.instance.loadedImageElement.subscribe(
        this.destroyHtmlImageAndRenderItInFabric.bind(this),
      );
    }
  }

  private destroyHtmlImageAndRenderItInFabric(imageElement: HTMLImageElement) {
    this.widthBeforeConvertingToFabricImage = this.width ?? 0;
    this.heightBeforeConvertingToFabricImage = this.height ?? 0;

    (this as fabric.Object as fabric.Image).setElement(imageElement, {});

    // Because after we call setElement, the fabric object will take the image dimensions, instead of the original pre-defined ones
    // We are re-scaling them to match the old dimensions,
    // We can't directly just re-set the width because that will crop the images
    this.imageUntouchedWidth = imageElement.naturalWidth;
    this.imageUntouchedHeight = imageElement.naturalHeight;

    this.setScaleBasedOnContent();

    this.componentRef?.destroy();
    this.componentRef?.changeDetectorRef.detectChanges();
    this.componentRef = undefined;
  }

  setScaleBasedOnContent() {
    if (
      this.widthBeforeConvertingToFabricImage === undefined ||
      this.heightBeforeConvertingToFabricImage === undefined
    ) {
      return;
    }

    // To scale the images with dimensions equal to the original content dimensions to maintain the original board image dimensions
    const neededScaleX = this.widthBeforeConvertingToFabricImage / (this.imageUntouchedWidth ?? 1);
    const neededScaleY =
      this.heightBeforeConvertingToFabricImage / (this.imageUntouchedHeight ?? 1);
    this.set({
      // Multiplying here by the already stored scale too, in case it has different value from 1
      // that is for the case of copying an image and pasting it, since the original one will also have a custom scale
      scaleX: neededScaleX * this.configurations.scaleX,
      scaleY: neededScaleY * this.configurations.scaleY,
      width: this.imageUntouchedWidth,
      height: this.imageUntouchedHeight,
    } as Partial<this>);
    this.setCoords();
    this.canvas?.renderAll();
  }

  protected syncCurrentObjectHtmlWithFabric(e: fabric.IEvent<Event> | { target: fabric.Object }) {
    super.syncCurrentObjectHtmlWithFabric(e);
    // we need to override pointer events that will be set on each syncCurrentObjectHtmlWithFabric call
    this.setPointerEventsBasedOnLoadingState();

    if (
      this.isOnStaticCanvas() &&
      this.componentRef?.instance.zoomLevel !== this.canvas?.getZoom()
    ) {
      // we need to update the zoom level if changed only
      this.componentRef?.setInput('zoomLevel', this.canvas?.getZoom());
      this.componentRef?.changeDetectorRef.detectChanges();
    }
  }

  private setPointerEventsBasedOnLoadingState() {
    const htmlElement = this.componentRef?.location?.nativeElement;
    if (htmlElement) {
      // Only the failed state has an interactive control (the reload button),
      // other states let events (e.g. wheel panning) pass through to the canvas
      htmlElement.style.pointerEvents =
        this.loadingState === ImageLoadingState.FailedToLoad ? 'all' : 'none';
    }
  }

  protected setInteractivityBasedOnLoadingState() {
    if (
      this.loadingState === ImageLoadingState.Loading ||
      this.loadingState === ImageLoadingState.DidNotAttemptToLoad
    ) {
      this.setUpControlsVisibility(Object.values(FabricControls));
      this.closeLock();
    } else {
      this.setUpControlsVisibility(ImageInvisibleControls);
      this.configureLocalLockState();
    }
  }

  signImageUrlIfNecessary() {
    // Signing private url here to guarantee that when accessing it is always a public url
    const url = this.configurations.url;
    if (url) {
      this.itemsCanvasService.getTrustedUrl(url).then((trustedUrl) => {
        this.configurations.url = trustedUrl;
        this.bindAngularComponentInputs();
      });
    }
  }

  protected override setStateFromConfigurations(configurations: ImageConfigurations) {
    super.setStateFromConfigurations(configurations);
    // After the initial load of the image in the HTML, we add it to fabric and,
    // When the image gets added to fabric, the width changes, to be the original width of the image,
    // Because previously we were scaling images by changing the width, but now we only scale them by changing the scale factors
    // Because changing the width/height in the fabric image, just crops them
    // So when we notice that the width is not the original image width, then the image must be cropped,
    // And we should re-fix the image like we do on the initial load
    // This case happens when the user clicks undo and that undo sets the width/height to the old one before being in fabric
    if (this.width !== this.imageUntouchedWidth) {
      this.setScaleBasedOnContent();
    }
  }

  protected bindAngularComponentInputs(): void {
    super.bindAngularComponentInputs();
    if (!this.componentRef) {
      return;
    }

    this.componentRef.setInput('state', this.configurations);
    this.componentRef.setInput('static', this.isOnStaticCanvas());
    this.componentRef.setInput('zoomLevel', this.canvas?.getZoom());

    if (this.customLoadingLayout) {
      this.componentRef.setInput('customLoadingLayout', true);
    }
    this.componentRef.changeDetectorRef.detectChanges();
  }

  isPdfPage() {
    return this.configurations.url?.startsWith(environment.pdfConversionsFolder) ?? false;
  }
}
